// src/pages/fleet/admin/RolesPage.tsx — Administration → Roles.
// A role is a name plus the Fleet pages it unlocks; users get the union of their roles.
import React, { useEffect, useState } from 'react';
import MasterTable, { FormValues } from '../master/MasterTable';
import type { FleetPermission, FleetRole } from '../../../types';
import { getRoles, createRole, updateRole, deleteRole } from '../../../services/fleetApi';
import { apiError } from '../../../utils/errors';
import { PERMISSIONS, permissionLabel } from '../permissions';

type RoleRow = FleetRole & { userCount: number; permCount: number };

export default function RolesPage() {
  const [rows, setRows] = useState<RoleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');

  const load = () => getRoles()
    .then((r) => {
      setRows((r.data.roles || []).map((x) => ({ ...x, userCount: x._count?.users ?? 0, permCount: x.permissions.length })));
      setLoadErr('');
    })
    .catch((e) => setLoadErr(apiError(e, 'Could not load roles')))
    .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const onSave = async (form: FormValues, existing: RoleRow | null) => {
    const body = { name: String(form.name ?? '').trim(), permissions: (form.permissions as FleetPermission[]) || [] };
    if (existing) await updateRole(existing.id, body);
    else await createRole(body);
    await load();
  };
  const onDelete = async (r: RoleRow) => { await deleteRole(r.id); await load(); };

  return (
    <MasterTable<RoleRow>
      icon="shield" title="Roles" addLabel="Role"
      subtitle={loading ? 'Loading…' : loadErr || `${rows.length} roles · decide which Fleet pages each user can open`}
      rows={rows}
      searchKeys={['name']}
      emptyText={loadErr || 'No roles yet — add one to start assigning access'}
      stats={[
        { label: 'Roles', value: rows.length, tint: 'var(--fx-accent)' },
        { label: 'Users assigned', value: rows.reduce((n, r) => n + r.userCount, 0), tint: 'var(--fx-green)' },
        { label: 'With Administration', value: rows.filter((r) => r.permissions.includes('admin')).length, tint: 'var(--fx-orange)' },
      ]}
      columns={[
        { key: 'name', label: 'Name', width: 200, render: (r) => <b>{r.name}</b> },
        { key: 'permCount', label: 'Permissions', render: (r) => <PermChips perms={r.permissions} />,
          csvValue: (r) => r.permissions.map(permissionLabel).join('; ') },
        { key: 'userCount', label: 'Users', width: 90, render: (r) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{r.userCount}</span> },
      ]}
      fields={[
        { key: 'name', label: 'Name', required: true, placeholder: 'e.g. Plants' },
        { key: 'permissions', label: 'Permission', type: 'checkboxes', required: true, default: [],
          options: PERMISSIONS.map((p) => ({ value: p.key, label: p.label, hint: p.hint })) },
      ]}
      toForm={(r) => ({ name: r.name, permissions: [...r.permissions] })}
      drawerWidth={520}
      onSave={onSave}
      onDelete={onDelete}
      rowLabel={(r) => r.name}
    />
  );
}

function PermChips({ perms }: { perms: FleetPermission[] }) {
  if (perms.length === PERMISSIONS.length) {
    return <span style={{ ...chip, background: 'var(--fx-accent-soft)', color: 'var(--fx-accent)' }}>All access</span>;
  }
  return (
    <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {PERMISSIONS.filter((p) => perms.includes(p.key)).map((p) => <span key={p.key} style={chip}>{p.label}</span>)}
    </span>
  );
}

const chip: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6,
  background: 'var(--fx-gray-soft)', color: 'var(--fx-text-2)', whiteSpace: 'nowrap',
};
